import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  InputAdornment,
  List,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useAtomValue } from 'jotai';
import ErrorMsg from '../common/ErrorMsg';
import OrgNewDialog from './OrgNewDialog';
import OrgSidebarItem from './OrgSidebarItem';
import PaginationNav from '../common/PaginationNav';
import usePaginatedOrgs from '../hooks/usePaginatedOrgs';
import { favoriteOrgsAtom } from '../../atoms/org-atoms';
import { IOrg } from '../../types/my-types';

const MIN_WIDTH = 220;
const MAX_WIDTH = 460;
const DEFAULT_WIDTH = 280;

function OrgSidebar() {
  const [label, setLabel] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [showNewOrgDialog, setShowNewOrgDialog] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const { data, loading, error, total, page, previousPage, nextPage, isFetching } =
    usePaginatedOrgs(label);
  const favoriteOrgs = useAtomValue(favoriteOrgsAtom);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      if (next !== label) setLabel(next);
    }, 600);
    return () => clearTimeout(timer);
  }, [searchInput, label]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e: MouseEvent) => {
      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, e.clientX - sidebarLeft)));
    };
    const handleMouseUp = () => setIsResizing(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  if (error) {
    return (
      <Box sx={{ width, p: 2 }}>
        <ErrorMsg error="Sorry, failed to load organizations." />
      </Box>
    );
  }

  const orgs: IOrg[] = data?.results || [];
  const orgLabels = orgs.map((o: IOrg) => o.label);

  const { favoriteOrgsList, otherOrgsList } = useMemo(() => {
    if (favoriteOrgs.length === 0) return { favoriteOrgsList: [], otherOrgsList: orgs };
    const favSet = new Set(favoriteOrgs);
    const favs: IOrg[] = [];
    const others: IOrg[] = [];
    for (const org of orgs) {
      if (favSet.has(org.id)) favs.push(org);
      else others.push(org);
    }
    return { favoriteOrgsList: favs, otherOrgsList: others };
  }, [orgs, favoriteOrgs]);

  return (
    <Box
      ref={sidebarRef}
      sx={{
        width,
        minWidth: width,
        height: '100%',
        bgcolor: '#fff',
        borderRight: '1px solid rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        flexShrink: 0,
        transition: isResizing ? 'none' : undefined,
        userSelect: isResizing ? 'none' : 'auto',
      }}
    >
      {/* Sidebar header: title + "+" button + search */}
      <Box sx={{ px: 2, pt: '14px', flexShrink: 0 }}>
        {/* Title row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' }}>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1.2px',
              color: '#5f6d7e',
            }}
          >
            Organizations
          </Typography>
          <Box
            component="button"
            onClick={() => setShowNewOrgDialog(true)}
            data-cy="accordion-create-org"
            title="New organization"
            sx={{
              width: 28,
              height: 28,
              borderRadius: '6px',
              border: '1px solid #BDC3D2',
              bgcolor: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#5f6d7e',
              flexShrink: 0,
              '&:hover': { bgcolor: '#F1F6F8', color: '#1D9AAA', borderColor: '#1D9AAA' },
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        {/* Search */}
        <TextField
          placeholder="Search organizations..."
          autoComplete="off"
          variant="outlined"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          data-cy="search-org"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#aab4c0' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '100%',
            mb: 1,
            '& .MuiOutlinedInput-root': {
              height: 36,
              borderRadius: '8px',
              bgcolor: '#F5F6FA',
              fontSize: '13px',
              '& fieldset': { borderColor: 'rgba(0,0,0,0.1)' },
              '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.2)' },
              '&.Mui-focused fieldset': {
                borderColor: '#1D9AAA',
                borderWidth: 1,
              },
              '&.Mui-focused': {
                bgcolor: '#fff',
                boxShadow: '0 0 0 3px rgba(29,154,170,0.1)',
              },
            },
            '& .MuiOutlinedInput-input': {
              py: 0,
              fontSize: '13px',
              '&::placeholder': { color: '#aab4c0', opacity: 1 },
            },
          }}
        />
      </Box>

      {/* New Organization Dialog */}
      <OrgNewDialog
        open={showNewOrgDialog}
        onClose={() => setShowNewOrgDialog(false)}
        orgLabels={orgLabels}
      />

      {/* Org list */}
      <List
        component="nav"
        data-cy="org-list"
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
          px: 1.5,
          mt: 0.5,
          opacity: isFetching && !loading ? 0.5 : 1,
          transition: 'opacity 0.2s',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#BCD3D2', borderRadius: 4 },
        }}
      >
        {loading ? (
          Array.from({ length: 10 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                px: 1.5,
                py: '10px',
                borderRadius: '8px',
                mb: 0.5,
              }}
            >
              <Skeleton
                animation="wave"
                variant="rounded"
                height={16}
                sx={{ borderRadius: '4px' }}
                width={`${45 + (i * 17) % 50}%`}
              />
            </Box>
          ))
        ) : (
          <>
            {favoriteOrgsList.length > 0 && (
              <>
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    color: '#8E99A8',
                    px: 0.5,
                    pt: 0.5,
                    pb: 0.5,
                  }}
                >
                  Favorites
                </Typography>
                {favoriteOrgsList.map((org: IOrg) => (
                  <OrgSidebarItem key={org.id} org={org} isCollapsed={false} />
                ))}
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    color: '#8E99A8',
                    px: 0.5,
                    pt: 1.5,
                    pb: 0.5,
                  }}
                >
                  All Organizations
                </Typography>
              </>
            )}
            {otherOrgsList.map((org: IOrg) => (
              <OrgSidebarItem key={org.id} org={org} isCollapsed={false} />
            ))}
          </>
        )}
      </List>

      {/* Pagination */}
      {!loading && !isFetching && total > 1 && (
        <Box
          sx={{
            px: 2,
            py: '10px',
            borderTop: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <PaginationNav
            total={total}
            page={page}
            previousPage={previousPage}
            nextPage={nextPage}
          />
        </Box>
      )}

      {/* Resize handle */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          top: 0,
          right: -3,
          width: 6,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&::after': {
            content: '""',
            width: 3,
            height: 40,
            borderRadius: 3,
            bgcolor: 'transparent',
            transition: 'background 0.2s',
          },
          '&:hover::after': { bgcolor: '#1D9AAA' },
          ...(isResizing && { '&::after': { content: '""', width: 3, height: 40, borderRadius: 3, bgcolor: '#1D9AAA' } }),
        }}
      />
    </Box>
  );
}

export default OrgSidebar;
